/**
 * Barra de ações para seleção múltipla de arquivos
 * Exibe ações disponíveis quando arquivos são selecionados
 */

import React from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Slide,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import DeselectIcon from '@mui/icons-material/Deselect'
import { FileItem } from '../services/api'

export type BatchAction = 'compare' | 'download' | 'delete'

type Props = {
  selectedFiles: FileItem[]
  totalFiles: number
  onSelectAll: () => void
  onClearSelection: () => void
  onAction: (action: BatchAction) => void
  onClose: () => void
}

export default function SelectionToolbar({
  selectedFiles,
  totalFiles,
  onSelectAll,
  onClearSelection,
  onAction,
  onClose
}: Props) {
  const count = selectedFiles.length
  const allSelected = count === totalFiles && totalFiles > 0
  const completedCount = selectedFiles.filter(f => f.status === 'completed').length

  // Comparação requer 2-4 arquivos completos
  const canCompare = completedCount >= 2 && completedCount <= 4
  // Download requer pelo menos 1 arquivo completo
  const canDownload = completedCount >= 1
  // Delete sempre disponível
  const canDelete = count >= 1

  return (
    <Slide direction="up" in={count > 0} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          borderRadius: 3,
          overflow: 'hidden',
          minWidth: 500,
          maxWidth: 700
        }}
      >
        <Box
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={count}
              size="small"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                fontWeight: 700,
                fontSize: '0.9rem'
              }}
            />
            <Typography variant="body2" fontWeight={500}>
              {count === 1 ? 'arquivo selecionado' : 'arquivos selecionados'}
            </Typography>
            {completedCount < count && (
              <Chip
                label={`${completedCount} completo${completedCount !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.5)', 
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.7rem'
                }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Tooltip title={allSelected ? 'Desmarcar Todos' : 'Selecionar Todos'}>
              <IconButton
                size="small"
                onClick={allSelected ? onClearSelection : onSelectAll}
                sx={{ color: 'white' }}
              >
                {allSelected ? <DeselectIcon /> : <SelectAllIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancelar Seleção">
              <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ p: 2, bgcolor: '#fff' }}>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Tooltip
              title={
                !canCompare
                  ? completedCount < 2
                    ? 'Selecione 2-4 arquivos completos para comparar'
                    : 'Máximo de 4 arquivos para comparação'
                  : 'Comparar transcrições selecionadas'
              }
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<CompareArrowsIcon />}
                  disabled={!canCompare}
                  onClick={() => onAction('compare')}
                  sx={{
                    bgcolor: '#2e7d32',
                    '&:hover': { bgcolor: '#1b5e20' }
                  }}
                >
                  Comparar ({completedCount})
                </Button>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            <Tooltip
              title={
                !canDownload
                  ? 'Selecione arquivos completos para download'
                  : 'Baixar transcrições selecionadas'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={!canDownload}
                  onClick={() => onAction('download')}
                >
                  Download ({completedCount})
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Excluir arquivos selecionados">
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={!canDelete}
                  onClick={() => onAction('delete')}
                >
                  Excluir ({count})
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>
    </Slide>
  )
}
